/**
 * Permissions Context
 * 
 * Manages user team permissions for page access control. Provides permissions
 * state and methods to check if a user has access to specific pages.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { teamService } from '@/features/settings/services/teamService.js';
import { profileService } from '@/features/settings/services/profileService.js';
import { tokenService } from '@/features/auth/services/tokenService.js';
import { authService } from '@/features/auth/services';
import logger from '@/shared/utils/logger.js';

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userTeams, setUserTeams] = useState([]);

  // Map of page paths to permission IDs
  const pagePathToPermissionId = useMemo(() => ({
    '/dashboard': 'dashboard',
    '/collections': 'collections',
    '/settlements': 'settlements',
    '/disbursements': 'disbursements',
    '/utilities': 'utilities',
    '/SMS': 'sms',
    '/payroll': 'payroll',
    '/reports': 'reports',
    '/settings': 'settings',
    '/invoicing': 'invoicing',
    '/checkout': 'checkout',
    '/settings/business/profile': 'business_profile',
    '/settings/individual/profile': 'business_profile',
    '/settings/business/security': 'security',
    '/settings/individual/security': 'security',
    '/settings/business/team': 'team_management',
    '/settings/business/api': 'api_settings',
    '/settings/individual/api': 'api_settings',
  }), []);

  // Load user permissions
  const loadPermissions = useCallback(async () => {
    try {
      setIsLoading(true);

      // Check if logout is in progress - if so, don't make any API calls
      const isLoggingOut = localStorage.getItem('isLoggingOut') === 'true';
      if (isLoggingOut) {
        logger.warn('Logout in progress, skipping permissions load');
        setPermissions([]);
        setUserTeams([]);
        setIsInitialized(true);
        return;
      }

      // Check if user is authenticated before making any API calls
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      const authToken = tokenService.getAccessToken();

      if (!isAuthenticated || !authToken) {
        logger.warn('User not authenticated, skipping permissions load');
        setPermissions([]);
        setUserTeams([]);
        setIsInitialized(true);
        return;
      }

      const userData = tokenService.getUserData();
      if (!userData) {
        setPermissions([]);
        setIsInitialized(true);
        return;
      }

      const userType = (userData.usertype || userData.user_type || userData.userType || '').toLowerCase();

      // 2. Grant full access if Business Owner (business_contact) or Individual user
      if (userType === 'individual' || userType === 'business_contact') {
        const fullAccess = [
          'dashboard', 'collections', 'settlements', 'disbursements',
          'utilities', 'sms', 'payroll', 'reports', 'settings',
          'invoicing', 'business_profile', 'security', 'team_management',
          'api_settings', 'payment_methods', 'checkout'
        ];
        setPermissions(fullAccess);
        localStorage.setItem('userPermissions', JSON.stringify(fullAccess));
        setIsInitialized(true);
        return;
      }

      // 3. For business_member, use aggregated permissions from login
      // Check priority: userData.permissions (fresh from login) > localStorage cache
      let userPerms = userData.permissions;

      if (!userPerms || !Array.isArray(userPerms)) {
        const cached = localStorage.getItem('userPermissions');
        if (cached) {
          try {
            userPerms = JSON.parse(cached);
          } catch (e) {
            logger.warn('Failed to parse cached permissions');
          }
        }
      }

      // If no permissions found, default to empty (only Settings accessible via hasPageAccess bypass)
      let finalPermissions = Array.isArray(userPerms) ? userPerms : [];
      
      // Ensure 'checkout' is always accessible for business_member accounts
      if (!finalPermissions.includes('checkout')) {
        finalPermissions = [...finalPermissions, 'checkout'];
      }
      
      setPermissions(finalPermissions);
      // userTeams is mostly used for the UI to list teams, we can keep it empty or load if needed
      setUserTeams(userData.userTeams || JSON.parse(localStorage.getItem('userTeams') || '[]'));
      
      if (finalPermissions.length > 0) {
        localStorage.setItem('userPermissions', JSON.stringify(finalPermissions));
      }

    } catch (error) {
      logger.error('Error loading permissions', { error });
      const cached = localStorage.getItem('userPermissions');
      if (cached) {
        try { setPermissions(JSON.parse(cached)); } catch (e) {}
      }
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, []);

  // Check if user has permission for a specific page
  const hasPermission = useCallback((pageId) => {
    try {
      if (!isInitialized) {return true;} // Allow access while loading
      if (permissions.length === 0) {return false;}

      // If permissions array is empty, deny access
      // If it contains 'all' or '*', allow all
      if (permissions.includes('all') || permissions.includes('*')) {
        return true;
      }

      return permissions.includes(pageId);
    } catch (error) {
      logger.error('Error in hasPermission', { error, pageId, permissions });
      // Default to allowing access on error to prevent complete lockout
      return true;
    }
  }, [permissions, isInitialized]);

  // Check if user has permission for a page path
  const hasPageAccess = useCallback((path) => {
    try {
      if (!isInitialized) {return true;} // Allow access while loading

      const userData = tokenService.getUserData();
      const userType = (userData?.usertype || userData?.user_type || '').toLowerCase();

      // Individual users have full access
      // business_contact (business owners) and business_member (team members) need permission checks
      if (userType !== 'business_contact' && userType !== 'business_member') {
        return true;
      }

      // Settings base path is always accessible (it will redirect to appropriate sub-path)
      // This allows the Settings component to handle the redirect logic
      if (path === '/settings' || path === '/settings/') {
        return true;
      }

      // For settings sub-paths, check specific permissions
      if (path.startsWith('/settings/')) {
        const permissionId = pagePathToPermissionId[path];
        if (permissionId) {
          return hasPermission(permissionId);
        }
        // If no specific permission mapping, allow access (Settings component will handle it)
        return true;
      }

      // Map path to permission ID
      const permissionId = pagePathToPermissionId[path] || path.replace(/^\//, '').replace(/\//g, '_');

      const hasAccess = hasPermission(permissionId);
      if (!hasAccess) {
        logger.warn(`PermissionsContext: Access denied for path '${path}' (permissionId: '${permissionId}')`);
        logger.debug('Current permissions:', permissions);
      }
      return hasAccess;
    } catch (error) {
      logger.error('Error in hasPageAccess', { error, path });
      // Default to allowing access on error to prevent complete lockout
      return true;
    }
  }, [hasPermission, isInitialized, pagePathToPermissionId]);

  // Refresh permissions (useful after team updates)
  const refreshPermissions = useCallback(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Initialize permissions on mount
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Listen for storage changes (when user logs in/out)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userData' || e.key === 'isAuthenticated') {
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        const authToken = tokenService.getAccessToken();

        if (!isAuthenticated || !authToken) {
          // Clear permissions on logout - don't make any API calls
          logger.debug('User logged out, clearing permissions without API calls');
          setPermissions([]);
          setUserTeams([]);
          setIsInitialized(false);
          localStorage.removeItem('userPermissions');
          localStorage.removeItem('userTeams');
        } else {
          // Only load permissions if user is authenticated and has a token
          loadPermissions();
        }
      }
    };

    // Also listen for custom events (for same-tab updates)
    const handleCustomStorage = () => {
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      const authToken = tokenService.getAccessToken();

      if (!isAuthenticated || !authToken) {
        // Clear permissions on logout - don't make any API calls
        logger.debug('User logged out (custom event), clearing permissions without API calls');
        setPermissions([]);
        setUserTeams([]);
        setIsInitialized(false);
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userTeams');
      } else {
        // Only load permissions if user is authenticated and has a token
        loadPermissions();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('storage', handleCustomStorage);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storage', handleCustomStorage);
    };
  }, [loadPermissions]);

  const value = {
    permissions,
    userTeams,
    isLoading,
    isInitialized,
    hasPermission,
    hasPageAccess,
    refreshPermissions,
    loadPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;

